import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { albumService } from "../services/albumService";
import { Album, CreateAlbumDto, UpdateAlbumDto } from "../types/album";
import { handleApiError, getErrorMessage } from "../utils/errorHandler";
import Session from "supertokens-auth-react/recipe/session";
import { authService } from "../services/authService";

export const albumKeys = {
  all: ['albums'] as const,
  lists: () => [...albumKeys.all, 'list'] as const,
  list: (ownerId: string) => [...albumKeys.lists(), ownerId] as const,
  details: () => [...albumKeys.all, 'detail'] as const,
  detail: (ownerId: string, albumId: string) => [...albumKeys.details(), ownerId, albumId] as const,
};

export const useAlbum = (ownerId?: string, albumId?: string) => {
  const {
    data: album,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: albumKeys.detail(ownerId!, albumId!),
    queryFn: async () => {
      if (!ownerId || !albumId) {
        throw new Error("Owner ID and Album ID are required");
      }

      await authService.checkAuthentication();

      return albumService.getAlbum(ownerId, albumId);
    },
    enabled: !!ownerId && !!albumId,
  });

  return {
    album,
    isLoading,
    error: error ? getErrorMessage(handleApiError(error)) : null,
    refetch,
  };
};

export const useAlbums = (ownerId?: string) => {
  const queryClient = useQueryClient();

  const {
    data: albums = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: albumKeys.list(ownerId!),
    queryFn: async () => {
      const sessionExists = ownerId ? await Session.doesSessionExist() : false;
      if (!ownerId || !sessionExists) {
        throw new Error("User not authenticated");
      }
      return albumService.getUserAlbums(ownerId);
    },
    enabled: !!ownerId,
  });

  const createAlbumMutation = useMutation({
    mutationFn: async (albumData: CreateAlbumDto) => {
      const sessionExists = ownerId ? await Session.doesSessionExist() : false;
      if (!ownerId || !sessionExists) {
        throw new Error("User not authenticated");
      }
      return albumService.createAlbum(ownerId, albumData);
    },
    onSuccess: (newAlbum) => {
      queryClient.setQueryData<Album[]>(albumKeys.list(ownerId!), (old) => {
        return old ? [...old, newAlbum] : [newAlbum];
      });
    },
    onError: (error) => {
      console.error("Error creating album:", handleApiError(error));
    },
  });

  const updateAlbumMutation = useMutation({
    mutationFn: async ({ albumId, updateData }: { albumId: string; updateData: UpdateAlbumDto }) => {
      const sessionExists = ownerId ? await Session.doesSessionExist() : false;
      if (!ownerId || !sessionExists) {
        throw new Error("User not authenticated");
      }
      return albumService.updateAlbum(ownerId, albumId, updateData);
    },
    onSuccess: (updatedAlbum) => {
      queryClient.setQueryData<Album[]>(albumKeys.list(ownerId!), (old) => {
        return old ? old.map((album) => (album.id === updatedAlbum.id ? updatedAlbum : album)) : [updatedAlbum];
      });
      queryClient.setQueryData(albumKeys.detail(ownerId!, updatedAlbum.id), updatedAlbum);
    },
    onError: (error) => {
      console.error("Error updating album:", handleApiError(error));
    },
  });

  const deleteAlbumMutation = useMutation({
    mutationFn: async (albumId: string) => {
      const sessionExists = ownerId ? await Session.doesSessionExist() : false;
      if (!ownerId || !sessionExists) {
        throw new Error("User not authenticated");
      }
      return albumService.deleteAlbum(ownerId, albumId);
    },
    onSuccess: (_, albumId) => {
      queryClient.setQueryData<Album[]>(albumKeys.list(ownerId!), (old) => {
        return old ? old.filter((album) => album.id !== albumId) : [];
      });
      queryClient.removeQueries({ queryKey: albumKeys.detail(ownerId!, albumId) });
    },
    onError: (error) => {
      console.error("Error deleting album:", handleApiError(error));
    },
  });

  return {
    // Data
    albums,
    hasAlbums: albums.length > 0,

    // Loading states
    isLoading,
    isCreating: createAlbumMutation.isPending,
    isUpdating: updateAlbumMutation.isPending,
    isDeleting: deleteAlbumMutation.isPending,

    // Error
    error: error ? getErrorMessage(handleApiError(error)) : null,

    // Actions
    createAlbum: createAlbumMutation.mutateAsync,
    updateAlbum: (albumId: string, updateData: UpdateAlbumDto) =>
      updateAlbumMutation.mutateAsync({ albumId, updateData }),
    deleteAlbum: deleteAlbumMutation.mutateAsync,
    refreshAlbums: refetch,
    clearError: () => {
      queryClient.resetQueries({ queryKey: albumKeys.list(ownerId!) });
    },

    // Query client for manual invalidation
    invalidateAlbums: () => queryClient.invalidateQueries({ queryKey: albumKeys.list(ownerId!) }),
  };
};